import Image from 'next/image';
import snacks from '@/assets/snacks.png';
import format from 'date-fns/format';
import { ArrowDownIcon } from '@heroicons/react/24/outline';

type PointsHistory = {
  _id: string;
  orderNo: string;
  status: string;
  pointsAwardedAt: Date;
  points: number;
};

type Reward = {
  _id: string;
  name: string;
  tier: 'Ruby' | 'Bronze' | 'Silver' | 'Gold' | 'Diamond' | 'Platinum' | string;
  description: string;
  pointsRequired: number;
  imageUrl: string;
};
type RedeemHistory = {
  _id: string;
  redeemedAt: string;
  pointsRequired: string;
  reward_id: {
    _id: string;
    pointsRequired: number;
    tier: string;
  };
};

type ManualHistory = {
  _id: string;
  points: number;
  orderNo: string;
  adminId: {
    first_name: string;
    role: string;
    outlet_id: {
      orderCode: string;
    };
  };
  assignedAt: Date;
};

type Props = {
  pointsHistory: PointsHistory[] | [];
  redeemHistory: RedeemHistory[] | [];
  activeReward: Reward | undefined;
  points: number | undefined;
  tier: string | undefined;
  manualHistory: ManualHistory[] | [];
};

const History = ({
  pointsHistory,
  redeemHistory,
  manualHistory,
  activeReward,
  points,
  tier,
}: Props) => {
  const normPointsHistory = pointsHistory.map((itm) => ({
    type: 'points',
    id: itm._id,
    date: new Date(itm.pointsAwardedAt),
    points: itm.points,
    orderNo: itm.orderNo,
  }));

  const normRedeemHistory = redeemHistory.map((itm) => ({
    type: 'redeem',
    id: itm._id,
    date: new Date(itm.redeemedAt),
    pointsRequired: itm.reward_id.pointsRequired,
  }));

  const normManualHistory = manualHistory.map((itm) => ({
    type: 'manual',
    id: itm._id,
    date: new Date(itm.assignedAt),
    points: itm.points,
    orderNo: itm.orderNo,
    role: itm.adminId?.role,
    name: itm.adminId?.first_name,
    outletCode: itm.adminId?.outlet_id?.orderCode ?? '',
  }));

  const allHistory = [
    ...normPointsHistory,
    ...normRedeemHistory,
    ...normManualHistory,
  ];
  
  const sortedHistory = allHistory.sort((a: any, b: any) => b.date - a.date);
  const point = [
    { tier: 'Ruby', point: 0 },
    { tier: 'Bronze', point: 20 },
    { tier: 'Silver', point: 50 },
    { tier: 'Gold', point: 100 },
    { tier: 'Diamond', point: 200 },
    { tier: 'Platinum', point: 300 },
  ];
  const startPoint = point.find(
    (itm) => itm.tier === activeReward?.tier
  )?.point;
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-base font-semibold mb-2">{tier} Cruncher</h2>
        <p className="text-sm text-gray-600 mb-2">
          {activeReward?.description}
        </p>

        <div className="w-full flex justify-between items-center gap-3">
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden mb-1">
            <div
              className={`h-full ${
                (points ?? 0) >= (activeReward?.pointsRequired ?? 0) ||
                tier === activeReward?.tier
                  ? 'bg-crunchies-red'
                  : 'bg-pink-200'
              }`}
              style={{
                width: `${
                  (((points ?? 0) - (startPoint ?? 0)) /
                    ((activeReward?.pointsRequired ?? 0) - (startPoint ?? 0))) *
                  100
                }%`,
              }}
            ></div>
          </div>
          <Image src={snacks} alt="Snacks" />
        </div>
        <p className="text-xs text-right mt-2 text-gray-500">
          {points?.toFixed(1)}/{activeReward?.pointsRequired} points
        </p>
      </div>

      <h3 className="text-base font-medium mb-2">Point history</h3>
      <div className="flex flex-col gap-4">
        {sortedHistory.map((item) => (
          <div key={item.id} className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {item.type === 'points' || item.type === 'manual' ? (
                <div className="p-2 bg-green-100 rounded-full">
                  <ArrowDownIcon className="w-5 h-5 text-green-900 rotate-[-60deg]" />
                </div>
              ) : (
                <div className="p-2 bg-red-100 rounded-full">
                  <ArrowDownIcon className="text-red-700 w-5 h-5 rotate-[120deg]" />
                </div>
              )}
              <div>
                {item.type === 'points' && 'orderNo' in item ? (
                  <p className="text-sm font-medium">{item.orderNo}</p>
                ) : item.type === 'manual' && 'orderNo' in item ? (
                  <p className="text-sm font-medium">{item.orderNo}</p>
                ) : (
                  <p className="text-sm font-medium">Redeemed</p>
                )}

                {item.type === 'points' ? (
                  <p className="text-xs text-[#475569]">
                    {format(item.date, 'dd MMM, yyyy h:mm a')}
                  </p>
                ) : item.type === 'manual' ? (
                  <p className="text-xs text-[#475569]">
                    {format(item.date, 'dd MMM, yyyy h:mm a')}
                  </p>
                ) : (
                  <p className="text-xs text-[#475569]">
                    {format(new Date(item.date), 'dd MMM, yyyy h:mm a')}
                  </p>
                )}

                {item.type === 'manual' && 'outletCode' in item && (
                  <p className="font-medium text-[#1E293B]">
                    Assigned By:
                    <span>
                      {item.outletCode ? String(item.outletCode) : 'HQ_Admin'}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <span className="font-semibold">
              {(item.type === 'points' || item.type === 'manual') &&
                'points' in item &&
                item.points}
              {item.type === 'redeem' &&
                'pointsRequired' in item &&
                item.pointsRequired}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default History;
